/*
 * Sneakies — PDP-only interactivity (sections/sneakies-pdp.liquid)
 *
 * The PDP buy box is driven entirely by assets/sn-preorder.js: this section
 * reuses that file's `sn-po-*` ids and `data-po-*` hooks, so pack switching,
 * plan cards, pricing, the frequency picker, the gallery and the nutrition
 * modal's open/close all come from there with no duplication. See the MIRRORS
 * note at the top of sections/sneakies-pdp.liquid.
 *
 * This file adds only what the PDP needs on top. Same conventions as the rest
 * of the sn-* set: no build step, no framework, one IIFE per feature, and each
 * feature bails when its elements are absent.
 */
(function () {
  var root = document.getElementById('sn-pdp-root');
  if (!root) return;

  /* ----------------------------------------------------------------------
   * Nutrition modal — focus trap and scroll lock
   *
   * sn-preorder.js already toggles this dialog's `hidden` attribute and closes
   * it on Escape. Rather than re-binding those same clicks here (two handlers
   * racing on one control is how double-toggles happen), this watches the
   * attribute and layers the accessibility behaviour a modal needs: the page
   * behind it must not scroll, Tab must not escape into it, and focus has to
   * return to whatever opened it.
   * -------------------------------------------------------------------- */
  (function initNutritionA11y() {
    var modal = document.getElementById('sn-po-nutrition');
    if (!modal || typeof MutationObserver !== 'function') return;

    var FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
    var lastFocused = null;
    var previousOverflow = '';

    function focusables() {
      return Array.prototype.slice.call(modal.querySelectorAll(FOCUSABLE)).filter(function (el) {
        // A hidden control is not a tab stop; offsetParent is null for those.
        return el.offsetParent !== null || el === document.activeElement;
      });
    }

    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      var items = focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      // Wrap in both directions, and pull stray focus back in if the active
      // element somehow sits outside the dialog.
      if (e.shiftKey && (document.activeElement === first || !modal.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !modal.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    }

    function open() {
      lastFocused = document.activeElement;
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeydown, true);
      var closeBtn = modal.querySelector('[data-po-nutrition-close]:not([aria-hidden="true"])');
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeydown, true);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
      lastFocused = null;
    }

    var wasHidden = modal.hidden;
    new MutationObserver(function () {
      if (modal.hidden === wasHidden) return;
      wasHidden = modal.hidden;
      if (modal.hidden) {
        close();
      } else {
        open();
      }
    }).observe(modal, { attributes: true, attributeFilter: ['hidden'] });
  })();

  /* ----------------------------------------------------------------------
   * "Meet the Squad" carousel arrows
   *
   * The track is a native scroll-snap row, so the arrows only need to nudge
   * scrollLeft by one card and let the browser snap. Deliberately does NOT wrap:
   * the design clamps at both ends, and the arrows disable there so the control
   * tells you when you've run out of cards.
   * -------------------------------------------------------------------- */
  (function initSquadCarousel() {
    var track = document.getElementById('sn-pdp-squad-track');
    if (!track) return;
    var prev = document.querySelector('[data-pd-squad-prev]');
    var next = document.querySelector('[data-pd-squad-next]');
    if (!prev || !next) return;

    function step() {
      var card = track.firstElementChild;
      if (!card) return track.clientWidth;
      // Card width plus the real gap, read from the computed style rather than
      // hardcoded, so the clamp()'d gap stays correct at every width.
      var gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
      return card.getBoundingClientRect().width + gap;
    }

    function syncArrows() {
      var maxScroll = track.scrollWidth - track.clientWidth;
      /*
       * The resting "start" position is NOT scrollLeft 0. The track has
       * horizontal padding and scroll-snap-type: x mandatory, so the browser
       * snaps the first card's start edge past that padding — measured at 4px
       * here, which a 1px tolerance mistook for "already scrolled". Derive the
       * tolerance from the actual padding instead of guessing a constant, so it
       * survives the clamp()'d padding changing at other widths.
       */
      var pad = parseFloat(getComputedStyle(track).paddingLeft || '0') || 0;
      prev.disabled = track.scrollLeft <= pad + 2;
      next.disabled = track.scrollLeft >= maxScroll - 2;
    }

    function nudge(dir) {
      var maxScroll = track.scrollWidth - track.clientWidth;
      var target = Math.max(0, Math.min(maxScroll, track.scrollLeft + dir * step()));
      if (typeof track.scrollTo === 'function') {
        track.scrollTo({ left: target, behavior: 'smooth' });
      } else {
        track.scrollLeft = target;
      }
    }

    prev.addEventListener('click', function () { nudge(-1); });
    next.addEventListener('click', function () { nudge(1); });
    track.addEventListener('scroll', syncArrows, { passive: true });
    window.addEventListener('resize', syncArrows);
    syncArrows();
  })();

  /* ----------------------------------------------------------------------
   * Sneak Peeks CTA
   *
   * The design's handler selects Subscribe & Save on the 16-pack and adds it to
   * the cart. Rather than reimplement that state change, this clicks the buy
   * box's own controls — so the visible selection, the hidden variant input and
   * the cart can never disagree — then submits through the theme's
   * product-form-component, which opens the cart drawer as usual.
   * -------------------------------------------------------------------- */
  (function initFlavorLabCta() {
    var cta = document.querySelector('[data-pd-flavorlab-cta]');
    if (!cta) return;

    cta.addEventListener('click', function () {
      var pack16 = document.querySelector('[data-po-pack][data-pack-key="16"]');
      if (pack16 && pack16.getAttribute('aria-checked') !== 'true') pack16.click();

      // Selecting the subscription means clicking whichever control is currently
      // offering it: the compact row when one-time is expanded, otherwise the
      // expanded card is already the subscription.
      var subCompact = document.getElementById('sn-po-option-sub-compact');
      if (subCompact && !subCompact.hidden) subCompact.click();

      var submit = document.getElementById('sn-po-cta');
      if (!submit || submit.disabled) return;
      // requestSubmit fires the form's submit event, which is what
      // product-form-component listens for; .submit() would bypass it.
      var form = submit.form;
      if (form && typeof form.requestSubmit === 'function') {
        form.requestSubmit(submit);
      } else {
        submit.click();
      }
    });
  })();
})();
